import React, { forwardRef } from 'react';
import { Drawer as MuiDrawer, StyledEngineProvider } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { InfoOutlined } from '@mui/icons-material';
import { Button, Tooltip } from '../index';

import './Drawer.scss';
import { classNames } from 'utilities/Utils';

type DrawerProps = {
  children: React.ReactNode;
  isOpen: boolean;
  title: string;
  onClose: () => void;
  onOutsideClick?: (
    event: {},
    reason: 'backdropClick' | 'escapeKeyDown',
  ) => void;
  subTitle?: string;
  onConfirm?: React.MouseEventHandler<HTMLButtonElement>;
  headerLeftElem?: React.ReactNode;
  tooltip?: React.ReactNode;
  tooltipClassName?: string;
  negativeText?: string;
  positiveText?: string;
  className?: string;
  hideFooter?: boolean;
  disablePositive?: boolean;
  showHeaderDivider?: boolean;
  newFooterCtas?: boolean;
  bannerHeight?: number;
};

const useStyles = makeStyles(() => ({
  footer: {
    backgroundColor: '#ffffff',
    boxShadow: '0 -2px 4px 0 rgba(0, 0, 0, 0.12)',
    padding: '15px 0',
    display: 'flex',
    justifyContent: 'center',
    position: 'sticky',
    bottom: 0,
    right: 0,
    width: '100%',

    '& .btn-cancel': {
      margin: '0 4px',
    },
    '& .btn-cancel-new': {
      margin: '0 4px',
      width: '40%',
    },
    '& .btn-confirm-new': {
      margin: '0 4px',
      width: '40%',
    },
  },
  backdrop: {
    backgroundColor: 'rgba(0, 0, 0, 0.25)',
  },
  bannerStyle: (props: { bannerHeight: number }) => ({
    height: `calc(100% - ${props.bannerHeight}px)`,
    top: `${props.bannerHeight}px`,
  }),
}));

function Drawer(props: DrawerProps, ref?: React.Ref<HTMLDivElement>) {
  const {
    children,
    isOpen,
    title,
    subTitle,
    onClose,
    onConfirm,
    onOutsideClick,
    headerLeftElem,
    tooltip,
    className,
    tooltipClassName,
    negativeText = 'Close',
    positiveText = 'Submit',
    hideFooter = false,
    disablePositive = false,
    showHeaderDivider = false,
    newFooterCtas = false,
    bannerHeight = 0,
  } = props;
  const classes = useStyles({ bannerHeight });

  const hideFooterCls = hideFooter ? 'hide-footer' : '';

  const containerClass = className
    ? `drawer-container ${className} ${hideFooterCls}`
    : `drawer-container ${hideFooterCls}`;

  const subTitleClassName = tooltip ? 'marginR24' : '';

  return (
    <StyledEngineProvider injectFirst>
      <MuiDrawer
        anchor="right"
        onClose={onOutsideClick ?? onClose}
        classes={{ paper: classNames(containerClass, classes.bannerStyle) }}
        ModalProps={{ BackdropProps: { className: classes.backdrop } }}
        open={isOpen}
      >
        <div className="drawer-content" ref={ref}>
          {title && (
            <div className="drawer-header">
              <div>
                {headerLeftElem && (
                  <div className="header-left-wrapper">{headerLeftElem}</div>
                )}
                <h1 className="drawer-title">{title}</h1>
              </div>
              {subTitle && (
                <div className="drawer-sub-header">
                  <p className={subTitleClassName}>{subTitle}</p>
                  {tooltip && (
                    <Tooltip
                      className={tooltipClassName}
                      content={tooltip}
                      placement="bottom-end"
                    >
                      <InfoOutlined />
                    </Tooltip>
                  )}
                </div>
              )}
              {showHeaderDivider && (
                <div className="drawer-header-divider"></div>
              )}
            </div>
          )}
          {children}
        </div>
        {!newFooterCtas ? (
          <div className={classes.footer}>
            {negativeText && (
              <Button
                className="btn-cancel"
                size="large"
                variant="text"
                onClick={onClose}
              >
                {negativeText}
              </Button>
            )}

            {onConfirm && positiveText && (
              <Button
                disabled={disablePositive}
                size="large"
                onClick={onConfirm}
              >
                {positiveText}
              </Button>
            )}
          </div>
        ) : (
          <div className={classes.footer}>
            {negativeText && (
              <Button
                className="btn-cancel-new marginR20"
                size="large"
                variant="outlined"
                onClick={onClose}
              >
                {negativeText}
              </Button>
            )}

            {onConfirm && positiveText && (
              <Button
                className="btn-confirm-new"
                disabled={disablePositive}
                size="large"
                onClick={onConfirm}
              >
                {positiveText}
              </Button>
            )}
          </div>
        )}
      </MuiDrawer>
    </StyledEngineProvider>
  );
}

export default forwardRef(Drawer);
